import React, { useState } from 'react';
import ParallaxSection from './ParallaxSection';
import { useScrollToContact } from '../hooks/useScrollToContact';

interface PriceItemProps {
  duration: string;
  price: string;
  description?: string;
}

const PriceItem: React.FC<PriceItemProps> = ({ duration, price, description }) => (
  <div className="border-b border-gray-200 py-3">
    <div className="flex justify-between items-center">
      <span className="font-medium">{duration}</span>
      <span className="text-blue-600 font-bold">{price}</span>
    </div>
    {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
  </div>
);

interface AccordionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const Accordion: React.FC<AccordionProps> = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white shadow overflow-hidden rounded-lg mb-4">
      <button
        className="w-full px-4 py-5 sm:px-6 bg-gray-50 flex justify-between items-center"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h3 className="text-lg leading-6 font-medium text-gray-900">{title}</h3>
        <svg 
          className={`h-5 w-5 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          {children}
        </div>
      )}
    </div>
  );
};

const BecomePilot: React.FC = () => {
  const scrollToContact = useScrollToContact();

  return (
    <ParallaxSection
      id="pilot-werden"
      backgroundImage="/images/Flugsportverein_Bilder_11.jpg"
    >
      <div>
        <div className="lg:text-center">
          <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">Ausbildung & Mitfliegen</h2>
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            Pilot werden
          </p>
          <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto mb-10">
            Entdecken Sie die Faszination des Fliegens und starten Sie Ihre Pilotenkarriere bei uns.
          </p>
        </div>

        <div className="mb-10">
          <Accordion title="Der Weg zum Flugschein">
            <p className="mb-4">
              Der Weg zum Flugschein führt bei uns über eine fundierte theoretische und praktische Ausbildung. 
              Sie können jederzeit mit der Ausbildung beginnen und in Ihrem eigenen Tempo lernen.
            </p>
            
            <h4 className="font-bold mt-4">Angebotene Lizenzen/Berechtigungen:</h4>
            <ul className="mt-2 space-y-2 pl-5" style={{ listStyleType: 'none' }}>
              <li>Sportpilotenlizenz (SPL) für Ultraleichtflugzeuge</li>
              <li>Segelflugzeugführerschein (GPL)</li>
            </ul>
            
            <p className="mt-4">
              <strong>Sie können jederzeit mit der Ausbildung beginnen!</strong> Wir bieten regelmäßig Theorieunterricht an und 
              die praktische Ausbildung ist ganzjährig möglich (wetterabhängig).
            </p>
           

          </Accordion>
          
          <Accordion title="Schnupperflüge & Pilot für einen Tag">
            <p className="mb-4">
              Sie sind sich noch nicht sicher, ob das Fliegen das Richtige für Sie ist? Kein Problem! 
              Wir bieten verschiedene Möglichkeiten, um in die Welt der Luftfahrt hineinzuschnuppern.
            </p>
            
            <h4 className="font-bold mt-4">Schnupperflüge:</h4>
            <p className="mt-2">
              Fliegen Sie mit einem unserer erfahrenen Piloten mit und erleben Sie die Faszination des Fliegens hautnah.
            </p>
            
            <div className="mt-4 bg-gray-50 p-4 rounded-md">
              <PriceItem duration="15 Minuten" price="30 €" />
              <PriceItem duration="30 Minuten" price="60 €" />
            </div>
            
            <h4 className="font-bold mt-6">Pilot für einen Tag:</h4>
            <p className="mt-2">
              Steigen Sie ins Cockpit und übernehmen Sie das Steuer! Ihr Erlebnis beinhaltet:
            </p>
            <ul className="mt-2 space-y-2 pl-5" style={{ listStyleType: 'none' }}>
              <li>1 Stunde Theorie und technisches Briefing</li>
              <li>Einführung in das Flugzeug</li>
              <li>Etwa 1 Stunde Flugzeit, in der Sie selbst fliegen</li>
              <li>Nachbesprechung und Auswertung des Fluges</li>
            </ul>
            <p className="mt-2 text-sm italic">Vorsicht: Dieses Erlebnis könnte Ihre Leidenschaft fürs Fliegen entfachen!</p>
            
            <div className="mt-4 bg-gray-50 p-4 rounded-md">
              <PriceItem duration="Pilot für einen Tag" price="199 €" description="Inkl. Theorie, Briefing, Flug und Debriefing, Dauer ca. 3 Stunden" />
            </div>
          </Accordion>
          
          <Accordion title="Wichtige Informationen">
            <div className="space-y-6">
              <div>
                <h4 className="font-bold">Flugbedingungen:</h4>
                <ul className="mt-2 space-y-2 pl-5" style={{ listStyleType: 'none' }}>
                  <li>Unsere Ultraleichtflugzeuge sind für zwei Personen zugelassen</li>
                  <li>Alle Flüge erfolgen unter Sichtflugregeln (VFR)</li>
                  <li>Wettervoraussetzungen: Kein Regen und kein starker oder böiger Wind</li>
                  <li>Der Pilot vor Ort entscheidet über die Durchführung des Fluges</li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold">Betriebszeiten:</h4>
                <p className="mt-2">
                  Von 30 Minuten vor Sonnenaufgang bis 30 Minuten nach Sonnenuntergang
                </p>
              </div>
              <div>
                <h4 className="font-bold">Bestellung von Rundflug-Gutscheinen:</h4>
                <div className="mt-2 space-y-2">
                  <div className="flex">
                    <span className="mr-2 font-medium">1.</span>
                    <span>Füllen Sie das Online-Formular auf unserer Website aus</span>
                  </div>
                  <div className="flex">
                    <span className="mr-2 font-medium">2.</span>
                    <span>Wählen Sie die gewünschte Dauer des Rundflugs</span>
                  </div>
                  <div className="flex">
                    <span className="mr-2 font-medium">3.</span>
                    <span>Sie erhalten den Gutschein sowie die Rechnung per E-Mail</span>
                  </div>
                  <div className="flex">
                    <span className="mr-2 font-medium">4.</span>
                    <span>Vereinbaren Sie anschließend einen Termin für Ihren Flug mit uns</span>
                  </div>
                </div>
              </div>
            </div>
          </Accordion>
        </div>

        <div className="text-center">
          <a
            href="#kontakt"
            onClick={scrollToContact}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
          >
            Jetzt Rundflug buchen oder Ausbildung starten!
          </a>
        </div>
      </div>
    </ParallaxSection>
  );
};

export default BecomePilot; 